import API from './axios';
import { getLabHospital } from '../utils/labHospital';

/**
 * Laboratory module API (docs/lab-module/04-api.md). Paths are relative,
 * prefixed `lab/`, with a trailing slash.
 *
 * A platform admin acts on one hospital's lab at a time: the chosen hospital
 * (utils/labHospital) is added as `?hospital=` to lab requests, except
 * `lab/admin/*`, which names the hospital explicitly.
 */
API.interceptors.request.use((config) => {
  const url = config.url || '';
  const hospital = getLabHospital();
  if (hospital && url.startsWith('lab/') && !url.startsWith('lab/admin/')) {
    config.params = { hospital, ...(config.params || {}) };
  }
  return config;
});

const L = (p) => `lab/${p}`;

export const labService = {
  // context & admin
  getMe: () => API.get(L('me/')),
  getStatus: (hospitalId) =>
    API.get(L('admin/status/'), { params: hospitalId ? { hospital: hospitalId } : {} }),
  enableLab: (hospitalId) =>
    API.post(L('admin/enable/'), hospitalId ? { hospital: hospitalId } : {}),
  disableLab: (hospitalId) =>
    API.post(L('admin/disable/'), hospitalId ? { hospital: hospitalId } : {}),

  // settings
  getSettings: () => API.get(L('settings/')),
  updateSettings: (data) => API.patch(L('settings/'), data),
  uploadLogo: (file) => {
    const form = new FormData();
    form.append('logo', file);
    return API.post(L('settings/logo/'), form);
  },

  // staff
  getStaff: (params) => API.get(L('staff/'), { params }),
  createStaff: (data) => API.post(L('staff/'), data),
  updateStaff: (id, data) => API.patch(L(`staff/${id}/`), data),
  resetStaffPassword: (id, data) => API.post(L(`staff/${id}/reset-password/`), data),
  /** The signatory's signature, printed on released reports. */
  uploadStaffSignature: (id, file) => {
    const form = new FormData();
    form.append('signature', file);
    return API.post(L(`staff/${id}/signature/`), form);
  },
  getPermissionMatrix: () => API.get(L('staff/permissions/')),
  /** ID badges (06 §4.9): several tile on one A4 sheet. */
  getBadgesPdf: (ids) =>
    API.get(L(`staff/${ids[0]}/badge.pdf`), { params: { ids: ids.join(',') }, responseType: 'blob' }),

  // patients
  getPatients: (params) => API.get(L('patients/'), { params }),
  getPatient: (id, params) => API.get(L(`patients/${id}/`), { params }),
  lookupPatient: (phone) => API.get(L('patients/lookup/'), { params: { phone } }),
  createPatient: (data) => API.post(L('patients/'), data),
  updatePatient: (id, data) => API.patch(L(`patients/${id}/`), data),
  reissuePortalToken: (id) => API.post(L(`patients/${id}/reissue-portal-token/`)),
  getPatientConsultations: (id) => API.get(L(`patients/${id}/consultations/`)),

  // catalogue
  getTests: (params) => API.get(L('catalogue/tests/'), { params }),
  getTest: (id) => API.get(L(`catalogue/tests/${id}/`)),
  createTest: (data) => API.post(L('catalogue/tests/'), data),
  updateTest: (id, data) => API.patch(L(`catalogue/tests/${id}/`), data),
  deactivateTest: (id) => API.delete(L(`catalogue/tests/${id}/`)),
  createPackage: (data) => API.post(L('catalogue/packages/'), data),
  updatePackage: (id, data) => API.patch(L(`catalogue/packages/${id}/`), data),
  retirePackage: (id) => API.delete(L(`catalogue/packages/${id}/`)),
  getCategories: () => API.get(L('catalogue/categories/')),
  getMethods: () => API.get(L('catalogue/methods/')),
  getGlobalCatalog: (params) => API.get(L('catalogue/global/'), { params }),
  importFromCatalog: (ids) => API.post(L('catalogue/import-from-catalog/'), { catalog_test_ids: ids }),
  getRateComparison: (id, params) => API.get(L(`catalogue/tests/${id}/rate-comparison/`), { params }),
  getTestSuggestions: (id) => API.get(L(`catalogue/tests/${id}/suggestions/`)),

  // clients
  getClients: (params) => API.get(L('clients/'), { params }),
  getClient: (id) => API.get(L(`clients/${id}/`)),
  createClient: (data) => API.post(L('clients/'), data),
  updateClient: (id, data) => API.patch(L(`clients/${id}/`), data),
  deleteClient: (id) => API.delete(L(`clients/${id}/`)),
  getClientRates: (id, params) => API.get(L(`clients/${id}/rates/`), { params }),
  putClientRates: (id, rows) => API.put(L(`clients/${id}/rates/`), rows),
  clearClientRate: (id, testId) => API.delete(L(`clients/${id}/rates/${testId}/`)),
  getClientDocuments: (id) => API.get(L(`clients/${id}/documents/`)),
  uploadClientDocument: (id, file, kind) => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    return API.post(L(`clients/${id}/documents/`), form);
  },
  deleteClientDocument: (id, docId) => API.delete(L(`clients/${id}/documents/${docId}/`)),
  getClientDocumentFile: (id, docId) =>
    API.get(L(`clients/${id}/documents/${docId}/file/`), { params: { download: 1 }, responseType: 'blob' }),

  // tariffs
  getTariffs: (params) => API.get(L('tariffs/'), { params }),
  createTariff: (data) => API.post(L('tariffs/'), data),
  updateTariff: (id, data) => API.patch(L(`tariffs/${id}/`), data),
  deleteTariff: (id) => API.delete(L(`tariffs/${id}/`)),
  cloneTariff: (id, data) => API.post(L(`tariffs/${id}/clone/`), data),
  bulkAdjustTariff: (id, data) => API.post(L(`tariffs/${id}/bulk-adjust/`), data),
  getTariffRates: (id) => API.get(L(`tariffs/${id}/rates/`)),
  putTariffRates: (id, rows) => API.put(L(`tariffs/${id}/rates/`), rows),
  clearTariffRate: (id, testId) => API.delete(L(`tariffs/${id}/rates/${testId}/`)),
  getTariffComparison: (params) => API.get(L('tariffs/compare/'), { params }),

  // bookings
  getBookings: (params) => API.get(L('bookings/'), { params }),
  getBooking: (id) => API.get(L(`bookings/${id}/`)),
  createBooking: (data) => API.post(L('bookings/'), data),
  splitBooking: (data) => API.post(L('bookings/split/'), data),
  updateBooking: (id, data) => API.patch(L(`bookings/${id}/`), data),
  rescheduleBooking: (id, data) => API.post(L(`bookings/${id}/reschedule/`), data),
  cancelBooking: (id, data) => API.post(L(`bookings/${id}/cancel/`), data),
  confirmBooking: (id, data) => API.post(L(`bookings/${id}/confirm/`), data || {}),
  completeBooking: (id) => API.post(L(`bookings/${id}/complete/`)),
  getBookingSiblings: (id) => API.get(L(`bookings/${id}/siblings/`)),
  getPendingOrders: (params) => API.get(L('bookings/pending-orders/'), { params }),
  getRequisitionPdf: (id) => API.get(L(`bookings/${id}/requisition.pdf`), { responseType: 'blob' }),

  // home collection: the desk side (04 §2.10)
  getOrders: (params) => API.get(L('collection/orders/'), { params }),
  getOrder: (id) => API.get(L(`collection/orders/${id}/`)),
  updateOrder: (id, data) => API.patch(L(`collection/orders/${id}/`), data),
  assignOrder: (id, data) => API.post(L(`collection/orders/${id}/assign/`), data),
  rescheduleOrder: (id, data) => API.post(L(`collection/orders/${id}/reschedule/`), data),
  cancelOrder: (id, data) => API.post(L(`collection/orders/${id}/cancel/`), data),
  setOrderStatus: (id, data) => API.post(L(`collection/orders/${id}/status/`), data),
  setOrderLabStatus: (id, data) => API.post(L(`collection/orders/${id}/lab-status/`), data),
  autoAssign: (data) => API.post(L('collection/orders/auto-assign/'), data),
  resequenceOrders: (data) => API.post(L('collection/orders/resequence/'), data),
  getRounds: (params) => API.get(L('collection/rounds/'), { params }),
  getAttemptReasons: () => API.get(L('collection/attempt-reasons/')),
  getAgents: (params) => API.get(L('staff/'), { params: { lab_role: 'phlebotomist', ...(params || {}) } }),
  createAgent: (data) => API.post(L('staff/'), { ...data, lab_role: 'phlebotomist' }),
  updateAgent: (id, data) => API.patch(L(`staff/${id}/`), data),

  // specimens
  getSpecimens: (params) => API.get(L('specimens/'), { params }),
  getSpecimenQueue: (params) => API.get(L('specimens/queue/'), { params }),
  accessionBooking: (bookingId) => API.post(L('specimens/accession/'), { booking_id: bookingId }),
  createSpecimen: (data) => API.post(L('specimens/'), data),
  getSpecimen: (accession) => API.get(L(`specimens/${encodeURIComponent(accession)}/`)),
  scanSpecimen: (accession, data) => API.post(L(`specimens/${encodeURIComponent(accession)}/scan/`), data || {}),
  getLabelsPdf: (params) => API.get(L('specimens/labels.pdf'), { params, responseType: 'blob' }),

  // results (worklist)
  getWorklist: (params) => API.get(L('results/'), { params }),
  getResult: (id) => API.get(L(`results/${id}/`)),
  saveResultValues: (id, data) => API.put(L(`results/${id}/values/`), data),
  verifyResult: (id, data) => API.post(L(`results/${id}/verify/`), data || {}),
  releaseResult: (id) => API.post(L(`results/${id}/release/`)),
  bulkRelease: (ids) => API.post(L('results/bulk-release/'), { result_ids: ids }),
  rerunResult: (id, data) => API.post(L(`results/${id}/rerun/`), data),
  logCriticalCall: (id, data) => API.post(L(`results/${id}/critical-call/`), data),

  // reports & templates
  getReports: (params) => API.get(L('reports/'), { params }),
  getReport: (bookingId) => API.get(L(`reports/${bookingId}/`)),
  getReportPdf: (bookingId, params) => API.get(L(`reports/${bookingId}/pdf/`), { params, responseType: 'blob' }),
  getReportTemplates: (params) => API.get(L('report-templates/'), { params }),
  createReportTemplate: (data) => API.post(L('report-templates/'), data),
  updateReportTemplate: (id, data) => API.patch(L(`report-templates/${id}/`), data),
  deleteReportTemplate: (id) => API.delete(L(`report-templates/${id}/`)),

  // quote & billing
  quote: (data) => API.post(L('quote/'), data),
  nextNumber: (params) => API.get(L('billing/next-number/'), { params }),
  getInvoices: (params) => API.get(L('billing/invoices/'), { params }),
  getInvoice: (id) => API.get(L(`billing/invoices/${id}/`)),
  createInvoice: (data) => API.post(L('billing/invoices/'), data),
  cancelInvoice: (id, data) => API.post(L(`billing/invoices/${id}/cancel/`), data),
  refundInvoice: (id, data) => API.post(L(`billing/invoices/${id}/refund/`), data),
  getInvoicePdf: (id) => API.get(L(`billing/invoices/${id}/pdf/`), { responseType: 'blob' }),
  getReceipts: (params) => API.get(L('billing/receipts/'), { params }),
  createReceipt: (data) => API.post(L('billing/receipts/'), data),
  getReceiptPdf: (id) => API.get(L(`billing/receipts/${id}/pdf/`), { responseType: 'blob' }),
  getCreditNotes: (params) => API.get(L('billing/credit-notes/'), { params }),
  createCreditNote: (data) => API.post(L('billing/credit-notes/'), data),
  getApprovals: (params) => API.get(L('billing/approvals/'), { params }),
  approveBill: (id, data) => API.post(L(`billing/approvals/${id}/approve/`), data || {}),
  rejectBill: (id, data) => API.post(L(`billing/approvals/${id}/reject/`), data),

  // shifts (day close)
  getCurrentShift: (params) => API.get(L('shifts/current/'), { params }),
  getShifts: (params) => API.get(L('shifts/'), { params }),
  openShift: (data) => API.post(L('shifts/open/'), data),
  closeShift: (id, data) => API.post(L(`shifts/${id}/close/`), data),
  getDayClosePdf: (id) => API.get(L(`shifts/${id}/slip.pdf`), { responseType: 'blob' }),

  // accounts
  getChart: (params) => API.get(L('accounts/chart/'), { params }),
  createAccount: (data) => API.post(L('accounts/chart/'), data),
  updateAccount: (code, data) => API.patch(L(`accounts/chart/${encodeURIComponent(code)}/`), data),
  getLedger: (params) => API.get(L('accounts/ledger/'), { params }),
  getTrialBalance: (params) => API.get(L('accounts/trial-balance/'), { params }),
  getProfitLoss: (params) => API.get(L('accounts/profit-and-loss/'), { params }),
  getDayBook: (params) => API.get(L('accounts/day-book/'), { params }),
  getDayBookPdf: (params) => API.get(L('accounts/day-book.pdf'), { params, responseType: 'blob' }),
  getAgeing: (params) => API.get(L('accounts/ageing/'), { params }),
  getReconciliation: () => API.get(L('accounts/reconciliation/')),
  getGstRegister: (params) => API.get(L('accounts/gst-register/'), { params }),
  getTdsRegister: (params) => API.get(L('accounts/tds-register/'), { params }),
  getPayablesAgeing: (params) => API.get(L('accounts/payables-ageing/'), { params }),
  getExpenses: (params) => API.get(L('accounts/expenses/'), { params }),
  createExpense: (data) => API.post(L('accounts/expenses/'), data),

  // inventory & purchases (04 §2.19)
  getVendors: (params) => API.get(L('inventory/vendors/'), { params }),
  createVendor: (data) => API.post(L('inventory/vendors/'), data),
  updateVendor: (id, data) => API.patch(L(`inventory/vendors/${id}/`), data),
  getStockItems: (params) => API.get(L('inventory/items/'), { params }),
  createStockItem: (data) => API.post(L('inventory/items/'), data),
  updateStockItem: (id, data) => API.patch(L(`inventory/items/${id}/`), data),
  getStockBatches: (params) => API.get(L('inventory/batches/'), { params }),
  issueStock: (data) => API.post(L('inventory/consume/'), data),
  adjustStock: (data) => API.post(L('inventory/adjust/'), data),
  getStockMoves: (params) => API.get(L('inventory/stock-moves/'), { params }),
  getPurchases: (params) => API.get(L('inventory/purchases/'), { params }),
  getPurchase: (id) => API.get(L(`inventory/purchases/${id}/`)),
  createPurchase: (data) => API.post(L('inventory/purchases/'), data),
  payPurchase: (id, data) => API.post(L(`inventory/purchases/${id}/pay/`), data),

  // reference labs and send-outs (04 §2.20)
  getReferenceLabs: (params) => API.get(L('reference-labs/'), { params }),
  createReferenceLab: (data) => API.post(L('reference-labs/'), data),
  updateReferenceLab: (id, data) => API.patch(L(`reference-labs/${id}/`), data),
  getSendOuts: (params) => API.get(L('sendouts/'), { params }),
  getSendOutQueue: () => API.get(L('sendouts/queue/')),
  createSendOut: (data) => API.post(L('sendouts/'), data),
  sendOutResult: (resultId, data) => API.post(L(`results/${resultId}/sendout/`), data),
  receiveSendOut: (id, data) => API.post(L(`sendouts/${id}/receive/`), data),
  paySendOut: (id, data) => API.post(L(`sendouts/${id}/pay/`), data),
  payReferenceLab: (data) => API.post(L('sendouts/pay/'), data),

  // referral payouts (04 §2.23)
  getCommissions: (params) => API.get(L('commissions/'), { params }),
  getCommission: (id) => API.get(L(`commissions/${id}/`)),
  previewCommissions: (data) => API.post(L('commissions/preview/'), data),
  runCommissions: (data) => API.post(L('commissions/run/'), data),
  payCommission: (id, data) => API.post(L(`commissions/${id}/pay/`), data),
  cancelCommission: (id, data) => API.post(L(`commissions/${id}/cancel/`), data),

  // tpa
  getDeductionReasons: () => API.get(L('tpa/deduction-reasons/')),
  getClaims: (params) => API.get(L('tpa/claims/'), { params }),
  getClaim: (id) => API.get(L(`tpa/claims/${id}/`)),
  createClaim: (data) => API.post(L('tpa/claims/'), data),
  transitionClaim: (id, data) => API.post(L(`tpa/claims/${id}/transition/`), data),
  settleClaim: (id, data) => API.post(L(`tpa/claims/${id}/settle/`), data),
  clearDisallowance: (id, data) => API.post(L(`tpa/claims/${id}/disallowance/`), data),
  getClaimBatches: (params) => API.get(L('tpa/batches/'), { params }),
  createClaimBatch: (data) => API.post(L('tpa/batches/'), data),
  submitClaimBatch: (id, data) => API.post(L(`tpa/batches/${id}/submit/`), data || {}),
  getAnnexurePdf: (id) => API.get(L(`tpa/batches/${id}/annexure.pdf`), { responseType: 'blob' }),
  getDeductionAnalysis: (params) => API.get(L('tpa/deduction-analysis/'), { params }),
  getPayerScorecard: () => API.get(L('tpa/payer-scorecard/')),
  getTpaReconciliation: (params) => API.get(L('tpa/reconciliation/'), { params }),
  getAlerts: () => API.get(L('alerts/')),

  // report dispatch (04 §2.22)
  getDispatches: (params) => API.get(L('dispatches/'), { params }),
  getReadyToSend: (params) => API.get(L('dispatches/ready/'), { params }),
  previewDispatch: (data) => API.post(L('dispatches/preview/'), data),
  sendDispatch: (data) => API.post(L('dispatches/'), data),
  retryDispatch: (id) => API.post(L(`dispatches/${id}/retry/`)),
  markDispatch: (id, data) => API.post(L(`dispatches/${id}/mark/`), data),
  getDispatchTemplates: (params) => API.get(L('dispatch-templates/'), { params }),
  createDispatchTemplate: (data) => API.post(L('dispatch-templates/'), data),
  updateDispatchTemplate: (id, data) => API.patch(L(`dispatch-templates/${id}/`), data),
  deleteDispatchTemplate: (id) => API.delete(L(`dispatch-templates/${id}/`)),
  getPyzapTemplates: () => API.get(L('dispatch-templates/pyzap/')),

  // appointment reminders (04 §2.24)
  getReminderQueue: (params) => API.get(L('reminders/queue/'), { params }),
  sendReminders: (data) => API.post(L('reminders/send/'), data),
  getRemindersSent: (params) => API.get(L('reminders/sent/'), { params }),

  // campaigns (04 §2.25)
  getCampaigns: (params) => API.get(L('campaigns/'), { params }),
  createCampaign: (data) => API.post(L('campaigns/'), data),
  updateCampaign: (id, data) => API.patch(L(`campaigns/${id}/`), data),
  deleteCampaign: (id) => API.delete(L(`campaigns/${id}/`)),
  getCampaignAudience: (id, params) => API.get(L(`campaigns/${id}/audience/`), { params }),
  previewCampaignAudience: (data, params) => API.post(L('campaigns/audience-preview/'), data, { params }),
  runCampaign: (id) => API.post(L(`campaigns/${id}/run/`), { confirm: true }),
  getCampaignSends: (params) => API.get(L('campaigns/sends/'), { params }),

  // instruments and interfacing (04 §2.21)
  getInstruments: (params) => API.get(L('instruments/'), { params }),
  createInstrument: (data) => API.post(L('instruments/'), data),
  updateInstrument: (id, data) => API.patch(L(`instruments/${id}/`), data),
  getInstrumentMappings: (id) => API.get(L(`instruments/${id}/mappings/`)),
  saveInstrumentMappings: (id, rows) => API.put(L(`instruments/${id}/mappings/`), rows),
  deleteInstrumentMapping: (id, mappingId) => API.delete(L(`instruments/${id}/mappings/${mappingId}/`)),
  postInstrumentMessage: (id, data) => API.post(L(`instruments/${id}/messages/`), data),
  getInstrumentMessages: (params) => API.get(L('instruments/messages/'), { params }),
  getSampleMessages: () => API.get(L('instruments/sample-messages/')),

  // quality control (04 §2.14)
  getQcRuns: (params) => API.get(L('qc/'), { params }),
  createQcRun: (data) => API.post(L('qc/'), data),
  getQcChart: (params) => API.get(L('qc/chart/'), { params }),

  // MIS and the dashboard (04 §2.26)
  getLabDashboard: (params) => API.get(L('analytics/dashboard/'), { params }),
  getMisSummary: (params) => API.get(L('analytics/summary/'), { params }),
  getRevenueTrend: (params) => API.get(L('analytics/revenue-trend/'), { params }),
  getPayerMix: (params) => API.get(L('analytics/payer-mix/'), { params }),
  getTestMix: (params) => API.get(L('analytics/test-mix/'), { params }),
  getDepartmentMix: (params) => API.get(L('analytics/department-mix/'), { params }),
  getReferrerMix: (params) => API.get(L('analytics/referrers/'), { params }),
  getTopPatients: (params) => API.get(L('analytics/top-patients/'), { params }),
  getTurnaround: (params) => API.get(L('analytics/turnaround/'), { params }),
  getProductivity: (params) => API.get(L('analytics/productivity/'), { params }),
  getClientPerformance: (params) => API.get(L('analytics/client-performance/'), { params }),
  getContribution: (params) => API.get(L('analytics/contribution/'), { params }),
  getCollectionAnalytics: (params) => API.get(L('analytics/collections/'), { params }),

  // system: channel status, retention purge, masters export (04 §2.27)
  getSystemStatus: () => API.get(L('system/status/')),
  purgeExpiredData: () => API.post(L('system/purge/')),
  exportMasters: () => API.get(L('system/export/'), { responseType: 'blob' }),
};

/** Token-addressed pages; no login. The 401 refresh never runs for these (api/axios.js). */
export const labPublicService = {
  getPortal: (token) => API.get(L(`public/portal/${token}/`)),
  getPortalPdf: (token, bookingId) =>
    API.get(L(`public/portal/${token}/reports/${bookingId}/pdf/`), { responseType: 'blob' }),
  getTracking: (token) => API.get(L(`public/track/${token}/`)),
};

/** The phlebotomist's own stops (04 §3, the LabMS mobile contract; 06 §3.27 My round). */
export const labAgentService = {
  getMe: () => API.get(L('agent/me/')),
  getSummary: (params) => API.get(L('agent/summary/'), { params }),
  logout: () => API.post(L('agent/logout/')),
  getOrders: (params) => API.get(L('agent/orders/'), { params }),
  getOrder: (id) => API.get(L(`agent/orders/${id}/`)),
  setStatus: (id, data) => API.post(L(`agent/orders/${id}/status/`), data),
  unableToCollect: (id, data) => API.post(L(`agent/orders/${id}/unable-to-collect/`), data),
  sendOtp: (id, data) => API.post(L(`agent/orders/${id}/otp/send/`), data),
  verifyOtp: (id, data) => API.post(L(`agent/orders/${id}/otp/verify/`), data),
  confirmDetails: (id, data) => API.post(L(`agent/orders/${id}/confirm-details/`), data),
  complete: (id, data) => API.post(L(`agent/orders/${id}/complete/`), data),
  getAttemptReasons: () => API.get(L('agent/attempt-reasons/')),
  getActiveTrip: () => API.get(L('agent/trip/active/')),
  startTrip: (data) => API.post(L('agent/trip/start/'), data || {}),
  endTrip: () => API.post(L('agent/trip/end/')),
  pingBatch: (pings) => API.post(L('agent/tracking/ping/batch/'), { pings }),
};

export const labCustomerService = {
  getLabBookings: (params) => API.get(L('customer/bookings/'), { params }),
  getLabReports: (params) => API.get(L('customer/reports/'), { params }),
  getLabReport: (bookingId) => API.get(L(`customer/reports/${bookingId}/`)),
  getLabReportPdf: (bookingId) => API.get(L(`customer/reports/${bookingId}/pdf/`), { responseType: 'blob' }),
  getLabBills: (params) => API.get(L('customer/invoices/'), { params }),
  getLabBillPdf: (id) => API.get(L(`customer/invoices/${id}/pdf/`), { responseType: 'blob' }),
};

export const labDoctorService = {
  getOrderableTests: (params) => API.get(L('doctor/test-picker/'), { params }),
  getLabOrders: (params) => API.get(L('doctor/orders/'), { params }),
  createLabOrder: (data) => API.post(L('doctor/orders/'), data),
  getLabOrderResults: (bookingId) => API.get(L(`doctor/results/${bookingId}/`)),
  getLabOrderPdf: (bookingId) => API.get(L(`doctor/results/${bookingId}/pdf/`), { responseType: 'blob' }),
};
